import { api, throwApiError } from "../../api/api";

export type EvaluationQuestionType =
  | "BOOLEAN"
  | "SCALE"
  | "TEXT"
  | "SINGLE_CHOICE"
  | "MULTI_CHOICE"
  | string;

export type CreateFreelancerEvaluationRequest = {
  registrationId: string;
  formId?: string;
  formName?: string;
  evaluatorId: string;
};

export type ListFreelancerEvaluationsQuery = {
  registrationId?: string;
  evaluatorId?: string;
  formId?: string;
  freelancerId?: string;
  storeId?: string;
  submitted?: boolean;
  includeAnswers?: boolean;
  from?: string;
  to?: string;
};

export type FreelancerPublic = {
  id: string;
  nomeCompleto: string;
  cpf: string;
  contato?: string | null;
  pix?: string | null;
  banco?: string | null;
  rua?: string | null;
  numero?: string | null;
  complemento?: string | null;
  bairro?: string | null;
  cidade?: string | null;
  estado?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type StorePublic = {
  id: string;
  nomeFantasia: string;
  endereco?: string | null;
  estado?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type StoreSlotPublic = {
  id: string;
  storeId: string;
  day: string;
  startAt: string;
  endAt: string;
  capacity: number;
  extraCapacity: number;
  isCanceled: boolean;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  store?: StorePublic;
};

export type SlotRegistrationPublic = {
  id: string;
  slotId: string;
  freelancerId: string;
  day: string;
  activeDayKey?: string | null;
  status: string;
  decidedAt?: string | null;
  decidedById?: string | null;
  vacancyOpenedForSlotId?: string | null;
  createdAt: string;
  updatedAt: string;
  freelancer?: FreelancerPublic;
  slot?: StoreSlotPublic;
};

export type EvaluationFormPublic = {
  id: string;
  name: string;
  version: number;
  isActive: boolean;
  archivedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type EvaluationQuestionOptionPublic = {
  id: string;
  questionId: string;
  label: string;
  value?: string | null;
  order: number;
};

export type EvaluationQuestionPublic = {
  id: string;
  formId: string;
  type: EvaluationQuestionType;
  prompt: string;
  required: boolean;
  order: number;
  minValue?: number | null;
  maxValue?: number | null;
  archivedAt?: string | null;
  options?: EvaluationQuestionOptionPublic[];
  createdAt: string;
  updatedAt: string;
};

export type FreelancerEvaluationAnswerPublic = {
  id: string;
  evaluationId: string;
  questionId: string;
  booleanValue?: boolean | null;
  intValue?: number | null;
  textValue?: string | null;
  optionId?: string | null;
  optionIds?: string[] | null;
  createdAt: string;
  updatedAt: string;
  question?: EvaluationQuestionPublic;
};

export type FreelancerEvaluationPublic = {
  id: string;
  registrationId: string;
  formId: string;
  evaluatorId: string;
  submittedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  form?: EvaluationFormPublic;
  registration?: SlotRegistrationPublic;
  answers?: FreelancerEvaluationAnswerPublic[];
};

export type FreelancerEvaluationAnswerUpsertRequest = {
  questionId: string;
  booleanValue?: boolean;
  intValue?: number;
  textValue?: string;
  optionId?: string;
  optionIds?: string[];
};

export type UpdateFreelancerEvaluationAnswersRequest = {
  answers: FreelancerEvaluationAnswerUpsertRequest[];
};

export const createFreelancerEvaluationApi = async (
  dto: CreateFreelancerEvaluationRequest
): Promise<FreelancerEvaluationPublic> => {
  try {
    const { data } = await api.post<FreelancerEvaluationPublic>("/freelancer-evaluations", dto);
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao criar avaliação do freelancer.");
    throw error;
  }
};

export const listFreelancerEvaluationsApi = async (
  query: ListFreelancerEvaluationsQuery = {}
): Promise<FreelancerEvaluationPublic[]> => {
  try {
    const { data } = await api.get<FreelancerEvaluationPublic[]>("/freelancer-evaluations", {
      params: query,
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao listar avaliações do freelancer.");
    throw error;
  }
};

export const getFreelancerEvaluationByIdApi = async (
  evaluationId: string
): Promise<FreelancerEvaluationPublic> => {
  try {
    const { data } = await api.get<FreelancerEvaluationPublic>(
      `/freelancer-evaluations/${evaluationId}`
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao buscar avaliação do freelancer.");
    throw error;
  }
};

export const getFreelancerEvaluationByRegistrationIdApi = async (
  registrationId: string
): Promise<FreelancerEvaluationPublic> => {
  try {
    const { data } = await api.get<FreelancerEvaluationPublic>(
      `/slot-registrations/${registrationId}/evaluation`
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao buscar avaliação pelo registro do turno.");
    throw error;
  }
};

export const updateFreelancerEvaluationAnswersApi = async (
  evaluationId: string,
  dto: UpdateFreelancerEvaluationAnswersRequest
): Promise<FreelancerEvaluationPublic> => {
  try {
    const { data } = await api.patch<FreelancerEvaluationPublic>(
      `/freelancer-evaluations/${evaluationId}/answers`,
      dto
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao atualizar respostas da avaliação.");
    throw error;
  }
};

export const submitFreelancerEvaluationApi = async (
  evaluationId: string
): Promise<FreelancerEvaluationPublic> => {
  try {
    const { data } = await api.post<FreelancerEvaluationPublic>(
      `/freelancer-evaluations/${evaluationId}/submit`
    );
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao submeter avaliação do freelancer.");
    throw error;
  }
};
